import cx from "classnames";
import * as React from "react";
import { forwardRef, ReactNode, Ref } from "react";
import { AnchorElementProps } from "@stenajs-webui/core";
import styles from "./Link.module.css";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface LinkRenderProps extends AnchorElementProps {
  className: string;
  children?: ReactNode;
  ref?: Ref<HTMLAnchorElement>;
}

export interface LinkProps extends AnchorElementProps {
  disabled?: boolean;
  disableTabIndex?: boolean;
  iconLeft?: IconDefinition;
  iconRight?: IconDefinition;
  variant?: LinkVariant;
  size?: LinkSize;
  /** Render the link as a custom element (e.g. a router link).
   * Spread the supplied props onto your element to inherit styling, ref and anchor props. */
  render?: (props: LinkRenderProps) => ReactNode;
}

export type LinkVariant = "standard" | "caption" | "overline" | "bold";
export type LinkSize = "large" | "medium" | "small" | "smaller";

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      className,
      onClick,
      tabIndex = 0,
      disableTabIndex,
      disabled,
      variant = "standard",
      size = "medium",
      iconLeft,
      iconRight,
      href,
      render,
      ...anchorProps
    },
    ref,
  ) => {
    const mergedClassName = cx(
      styles.link,
      styles[variant],
      styles[size],
      disabled && styles.disabled,
      className,
    );

    const inner = (
      <>
        {iconLeft && (
          <span className={styles.iconLeft}>
            <FontAwesomeIcon icon={iconLeft} />
          </span>
        )}
        {children}
        {iconRight && (
          <span className={styles.iconRight}>
            <FontAwesomeIcon icon={iconRight} />
          </span>
        )}
      </>
    );

    if (render) {
      return render({
        ...anchorProps,
        tabIndex: !disableTabIndex ? tabIndex : undefined,
        className: mergedClassName,
        href: disabled ? undefined : href,
        onClick: !disabled ? onClick : undefined,
        ref,
        children: inner,
      });
    }

    return (
      <a
        tabIndex={!disableTabIndex ? tabIndex : undefined}
        className={mergedClassName}
        ref={ref}
        href={disabled ? undefined : href}
        onClick={!disabled ? onClick : undefined}
        {...anchorProps}
      >
        {inner}
      </a>
    );
  },
);
